/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   10/15/21
 */

import { cacheExchange, ResolveInfo, } from "@urql/exchange-graphcache";
import {
	Calendar,
	Employee,
	EventSubscription,
	Holiday,
	OpsGenieEvent,
	SubscriptionResponse,
	SummaryNotification,
	VegaEvent,
	WorkdayEvent
} from "../../interfaces/Global";
import {
	CalendarEventsQuery,
	CalendarEventsQueryVars,
	CalendarEventsResponse
} from "../../graphql/queries/calendar-events";
import { addMinutes, areIntervalsOverlapping, differenceInHours, format, formatDistanceStrict } from "date-fns";
import { format as formatDate } from "../../dates-helper";
import { SavedCalendarsQuery, SavedCalendarsQueryVars, SavedCalendarsResponse } from "../../graphql/queries/calendars";
import {
	EventSubscriptionsQuery,
	EventSubscriptionsQueryVars,
	EventSubscriptionsResponse
} from "../../graphql/queries/event-subscriptions";
import { schema } from "../../graphql/schema";
import { CalendarEventInput } from "../../graphql/mutations/calendar-events";
import {
	SummaryNotificationsQuery,
	SummaryNotificationsQueryVars,
	SummaryNotificationsResponse
} from "../../graphql/queries/summary-notifications";
import { toast } from "react-toastify";
import { getEventType } from "../../calendar/event-types";

const transformToDateTime = (parent: any, _args: any, _cache: any, info: ResolveInfo) =>
	(parent[info.fieldName] ? new Date(parent[info.fieldName]) : null);

const transformToDate = (parent: any, _args: any, _cache: any, info: ResolveInfo) => {
	if (parent[info.fieldName]) {
		const date = new Date(parent[info.fieldName]);
		const tzOffset = date.getTimezoneOffset();
		return addMinutes(date, tzOffset);
	}
	return null;
}

const formatEventDistance = (startDate: string | Date, endDate: string | Date) => {
	const diff = differenceInHours(new Date(endDate), new Date(startDate)), start = new Date(startDate), end = new Date(endDate);
	return diff === 24 ? formatDate(start, 'PP') + " (1 day)" :
		diff % 24 ? formatDate(start, 'Pp') + " - " + formatDate(end, 'Pp') + " (" + formatDistanceStrict(end, start) + ")" :
			formatDate(start, 'P') + " - " + formatDate(end, 'P') + " (" + formatDistanceStrict(end, start) + ")"
}

export default function getCacheExchange() {

	return cacheExchange({
		schema,
		keys: {
			EmployeesList: () => null,
			CalendarEventsList: () => null,
			HolidaysList: () => null,
			SavedCalendarsList: () => null,
			WorkdayEventsList: () => null,
			OpsGenieEventsList: () => null,
			EventSubscriptionsList: () => null,
			SummaryNotificationsList: () => null,
			SlackUserConversationsListResponse: () => null
		},
		resolvers: {
			Query: {
				"employee": (_, args) => ({ __typename: 'Employee', id: args.id }),
			},
			CalendarEvent: {
				creationDate: transformToDateTime,
				modifiedDate: transformToDateTime,
				startDatetime: transformToDateTime,
				endDatetime: transformToDateTime,
				confirmationDate: transformToDateTime
			} as Record<keyof VegaEvent, () => any>,
			Holiday: {
				startDate: transformToDate,
				endDate: transformToDate,
			} as Record<keyof Holiday, () => any>,
			Employee: {
				whenCreated: transformToDateTime,
				whenChanged: transformToDateTime,
				dateOfBirth: transformToDate,
				dateOfHire: transformToDate
			} as Record<keyof Employee, () => any>,
			SavedCalendar: {
				creationDate: transformToDateTime,
				modifiedDate: transformToDateTime,
				lastAccessed: transformToDateTime
			} as Record<keyof Calendar, () => any>,
			WorkdayEvent: {
				startDatetime: transformToDateTime,
				endDatetime: transformToDateTime,
			} as Record<keyof WorkdayEvent, () => any>,
			OpsGenieEvent: {
				startDatetime: transformToDateTime,
				endDatetime: transformToDateTime,
			} as Record<keyof OpsGenieEvent, () => any>,
			EventSubscription: {
				creationDate: transformToDateTime,
				modifiedDate: transformToDateTime,
				lastTriggered: transformToDateTime
			} as Record<keyof EventSubscription, () => any>,
			SummaryNotification: {
				creationDate: transformToDateTime,
				modifiedDate: transformToDateTime,
			} as Record<keyof SummaryNotification, () => any>
		},
		optimistic: {
			createCalendarEvent: (args) => {
				const input = args.data as Required<CalendarEventInput>;
				return {
					__typename: 'CalendarEvent',
					...input,
					id: Number(format(new Date(), 'Hms')),
					createdBy: "_",
					creationDate: new Date(),
					createdByEmployee: {
						id: "_system",
						displayName: 'System'
					},
					modifiedBy: "_",
					modifiedDate: new Date(),
					modifiedByEmployee: {
						id: "_system",
						displayName: 'System'
					},
					confirmationDate: input.isConfirmed ? new Date() : null,
					confirmedBy: "_",
					confirmedByEmployee: {
						id: "_system",
						displayName: 'System'
					},
					isUnavailable: true,
					vstamp: 0,
					properties: {
						loading: true
					}
				};
			},
		},
		updates: {
			Subscription: {
				calendarEvents: (_result, args, cache, _info) => {
					const res = _result.calendarEvents as SubscriptionResponse<VegaEvent>;
					const userId = JSON.parse(localStorage.getItem("vega.state.user") || "{}")?.id;
					if (res.event === "CALENDAR_EVENT_CREATED") {
						if (userId !== res.data.createdBy) {
							toast.info([
								(res.data.createdByEmployee?.displayName ?? res.data.createdBy),
								'created new event',
								res.data.createdBy !== res.data.employeeId ? "for " + (res.data.employee?.displayName ?? res.data.employeeId) : '',
								formatEventDistance(res.data.startDatetime, res.data.endDatetime),
								" - " + (getEventType(res.data.typeId)?.name ?? res.data.typeId)
							].join(" "));
						}
						cache.inspectFields('Query').filter(field => field.fieldName === "calendarEvents").forEach(field => {
							const {employeeIdIn, startDatetimeLt, endDatetimeGt} = (field.arguments as { employeeIdIn: string[], startDatetimeLt: Date, endDatetimeGt: Date}) || {};
							if (employeeIdIn.includes(res.data.employee?.id||"")
								&& areIntervalsOverlapping(
									{ start: new Date(endDatetimeGt), end: new Date(startDatetimeLt) },
									{ start: new Date(res.data.startDatetime), end: new Date(res.data.endDatetime) }
								)) {
								cache.updateQuery<CalendarEventsResponse, CalendarEventsQueryVars>(
									{ query: CalendarEventsQuery, variables: field.arguments ?? undefined },
									data => {
										if (!data) {
											return {
												__typename: "CalendarEventsList",
												calendarEvents: {
													total: 1,
													items: [res.data]
												}
											};
										}
										const optimisticEvent = data.calendarEvents.items.find(e => e.createdBy === "_" && new Date(e.startDatetime).getTime() === new Date(res.data.startDatetime).getTime()
											&& new Date(e.endDatetime).getTime() === new Date(res.data.endDatetime).getTime() && e.typeId === res.data.typeId && e.employeeId === res.data.employeeId);
										if (optimisticEvent) {
											data.calendarEvents.total--;
											data.calendarEvents.items = data.calendarEvents.items.filter(e => e.id !== optimisticEvent.id);
										}
										if (!data.calendarEvents.items.some(d => d.id === res.data.id)) {
											data.calendarEvents.total++;
											data.calendarEvents.items.push(res.data);
										}
										return data;
									}
								);
							}
						});
					} else if (res.event === "CALENDAR_EVENT_DELETED") {
						if (userId !== res.data.modifiedBy) {
							toast.info([
								(res.data.createdByEmployee?.displayName ?? res.data.createdBy),
								'deleted an event',
								res.data.createdBy !== res.data.employeeId ? "for " + (res.data.employee?.displayName ?? res.data.employeeId) : '',
								formatEventDistance(res.data.startDatetime, res.data.endDatetime),
								" - " + (getEventType(res.data.typeId)?.name ?? res.data.typeId)
							].join(" "));
						}
						cache.inspectFields('Query').filter(field => field.fieldName === 'calendarEvents').forEach(field => {
							cache.updateQuery<CalendarEventsResponse>(
								{ query: CalendarEventsQuery, variables: field.arguments ?? undefined },
								data => {
									if (!data) return null;
									data.calendarEvents.total--;
									data.calendarEvents.items = data.calendarEvents.items.filter(d => d.id !== res.data.id);
									return data;
								}
							);
						});
					} else {
						if (userId !== res.data.modifiedBy) {
							toast.info([
								(res.data.modifiedByEmployee?.displayName ?? res.data.modifiedBy),
								'updated an event',
								res.data.createdBy !== res.data.employeeId ? "for " + (res.data.employee?.displayName ?? res.data.employeeId) : '',
								formatEventDistance(res.data.startDatetime, res.data.endDatetime),
								" - " + (getEventType(res.data.typeId)?.name ?? res.data.typeId)
							].join(" "));
						}
					}
				}
			},
			Mutation: {
				createCalendarEvent: (_result, args, cache, _info) => {
					const model = _result.createCalendarEvent as VegaEvent;
					cache.inspectFields('Query').filter(field => field.fieldName === "calendarEvents").forEach(field => {
						cache.updateQuery<CalendarEventsResponse, CalendarEventsQueryVars>(
							{ query: CalendarEventsQuery, variables: field.arguments ?? undefined },
							data => {
								if (!data) {
									return {
										__typename: "CalendarEventsList",
										calendarEvents: {
											total: 1,
											items: [model]
										}
									};
								}
								if (!data.calendarEvents.items.some(d => d.id === model.id)) {
									data.calendarEvents.total++;
									data.calendarEvents.items.push(model);
								}
								return data;
							}
						);
					});
				},
				createSavedCalendar: (_result, args, cache, _info) => {
					const model = _result.createSavedCalendar as Calendar;
					cache.inspectFields('Query').filter(field => field.fieldName === "savedCalendars").forEach(field => {
						const { createdBy, orderBy } = (field.arguments as { createdBy?: string, orderBy?: string }) || {};
						if (createdBy) {
							cache.updateQuery<SavedCalendarsResponse, SavedCalendarsQueryVars>(
								{ query: SavedCalendarsQuery, variables: { createdBy, orderBy } },
								data => {
									if (!data) {
										return {
											__typename: "SavedCalendarsList",
											savedCalendars: {
												total: 1,
												items: [model]
											}
										};
									}
									if (!data.savedCalendars.items.some(d => d.id === model.id)) {
										data.savedCalendars.total++;
										data.savedCalendars.items.push(model);
									}
									return data;
								}
							);
						}
					});
				},
				createEventSubscription: (_result, args, cache, _info) => {
					const model = _result.createEventSubscription as EventSubscription;
					cache.inspectFields('Query').filter(field => field.fieldName === "eventSubscriptions").forEach(field => {
						const { createdBy } = (field.arguments as { createdBy?: string }) || {};
						if (createdBy) {
							cache.updateQuery<EventSubscriptionsResponse, EventSubscriptionsQueryVars>(
								{ query: EventSubscriptionsQuery, variables: { createdBy } },
								data => {
									if (!data) {
										return {
											__typename: "EventSubscriptionsList",
											eventSubscriptions: {
												total: 1,
												items: [model]
											}
										};
									}
									if (!data.eventSubscriptions.items.some(d => d.id === model.id)) {
										data.eventSubscriptions.total++;
										data.eventSubscriptions.items.push(model);
									}
									return data;
								}
							);
						}
					});
				},
				deleteSavedCalendar: (_result, args, cache, _info) => {
					const id = args.id;
					cache.inspectFields('Query').filter(field => field.fieldName === 'savedCalendars').forEach(field => {
						cache.updateQuery<SavedCalendarsResponse>(
							{ query: SavedCalendarsQuery, variables: field.arguments ?? undefined },
							data => {
								if (!data) return null;
								data.savedCalendars.total--;
								data.savedCalendars.items = data.savedCalendars.items.filter(d => d.id !== id);
								return data;
							}
						);
					});
				},
				deleteEventSubscription: (_result, args, cache, _info) => {
					const id = args.id;
					cache.inspectFields('Query').filter(field => field.fieldName === 'eventSubscriptions').forEach(field => {
						cache.updateQuery<EventSubscriptionsResponse, EventSubscriptionsQueryVars>(
							{ query: EventSubscriptionsQuery, variables: field.arguments ?? undefined },
							data => {
								if (!data) return null;
								data.eventSubscriptions.total--;
								data.eventSubscriptions.items = data.eventSubscriptions.items.filter(d => d.id !== id);
								return data;
							}
						);
					});
				},
				deleteSummaryNotification: (_result, args, cache, _info) => {
					const id = args.id;
					cache.inspectFields('Query').filter(field => field.fieldName === 'summaryNotifications').forEach(field => {
						cache.updateQuery<SummaryNotificationsResponse, SummaryNotificationsQueryVars>(
							{ query: SummaryNotificationsQuery, variables: field.arguments ?? undefined },
							data => {
								if (!data) return null;
								data.summaryNotifications.total--;
								data.summaryNotifications.items = data.summaryNotifications.items.filter(d => d.id !== id);
								return data;
							}
						);
					});
				}
			}
		}
	})
}